/**
 * Shared blank-name lookup for the shop-floor pull lists. Both the cross-batch press station and the
 * gang-sheet pick list turn an item's `resolvedBlankSku` into a human blank name, so the query hook
 * lives here (one place) instead of a per-page copy.
 */
import type { BlankOnHand } from '@fsp/contracts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApiClient } from '@/lib/api/hooks';

/**
 * Blank names keyed by bare `ssSku` (`GET /inventory`), for labelling a "blanks to pull" list. Shares
 * the `['inventory']` cache with the inventory pages, so it's usually already warm; a miss just falls
 * back to the product title, so a load failure degrades gracefully (the pull list still renders,
 * labelled by product).
 */
export function useBlankNames(): Map<string, BlankOnHand> {
  return useBlankNamesQuery().nameBySku;
}

/**
 * The same lookup plus its LOAD STATE, for callers that must not render until the catalogue has
 * actually arrived.
 *
 * ⚠️ Why this exists: `useBlankNames` returns `new Map()` while the query is still pending, and that is
 * INDISTINGUISHABLE from a resolved-but-empty catalogue. A pull list may treat the two alike (it just
 * labels a row by product title). A REPRINTED LABEL may not: an empty map silently swaps the blank name
 * and drops the colour, so the reissued tag differs from the one it replaces
 * (`docs/label-mislabel-mitigation-plan-v1.md` § 8.3). Such a caller gates on `isResolved`.
 */
export function useBlankNamesQuery(): {
  nameBySku: Map<string, BlankOnHand>;
  isResolved: boolean;
  isError: boolean;
} {
  const client = useApiClient();
  const { data, isSuccess, isError } = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load blanks.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items;
    },
    staleTime: 5 * 60_000,
  });
  const nameBySku = useMemo(() => new Map((data ?? []).map((b) => [b.ssSku, b])), [data]);
  // `isSuccess`, not `!isPending`: an ERRORED query is also "not pending", and treating a failed load
  // as resolved is precisely the silent-swap above.
  return { nameBySku, isResolved: isSuccess, isError };
}
